import { useState, useEffect, useCallback } from "react";
import {
  DragDropContext,
  Droppable,
  Draggable,
  type DropResult,
} from "@hello-pangea/dnd";
import { Button } from "@/components/ui/button";
import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";
import { TaskCard } from "./TaskCard";
import { TaskCreateDialog } from "./TaskCreateDialog";

type TaskStatus = "todo" | "in_progress" | "done";

interface TaskRecord {
  id: number;
  text: string;
  description?: string;
  contact_id?: number | null;
  assigned_to?: number;
  priority: string;
  source: string;
  due_date?: string;
  done_date?: string;
  completed_at?: string;
  created_at?: string;
  dismissed?: boolean;
  type?: string;
  contacts?: {
    first_name: string;
    last_name: string;
    current_score?: number;
    current_tier?: string;
  } | null;
}

const columns: Array<{ id: TaskStatus; title: string }> = [
  { id: "todo", title: "To Do" },
  { id: "in_progress", title: "In Progress" },
  { id: "done", title: "Done" },
];

function getTaskStatus(task: TaskRecord): TaskStatus {
  if (task.type === "done" || task.done_date || task.completed_at)
    return "done";
  if (task.type === "in_progress") return "in_progress";
  return "todo";
}

export function MyTasksBoard({
  salesId,
  showAll = false,
}: {
  salesId?: number;
  showAll?: boolean;
}) {
  const [tasks, setTasks] = useState<TaskRecord[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const loadTasks = useCallback(async () => {
    setLoadError(null);

    // Fetch tasks without a join to avoid join-syntax compatibility issues
    let query = supabase
      .from("tasks")
      .select("*")
      .order("created_at", { ascending: false });

    if (!showAll && salesId) {
      query = query.or(`assigned_to.eq.${salesId},sales_id.eq.${salesId}`);
    }

    const { data: taskData, error: taskError } = await query;
    if (taskError) {
      console.error("Failed to load tasks:", taskError);
      setLoadError(taskError.message);
      return;
    }

    if (!taskData) return;

    // Filter out dismissed tasks (dismissed may be null on old rows)
    const activeTasks = taskData.filter((t: TaskRecord) => !t.dismissed);

    // Collect unique contact IDs for a separate fetch
    const contactIds = [
      ...new Set(
        activeTasks
          .map((t: TaskRecord) => t.contact_id)
          .filter((id): id is number => id != null),
      ),
    ];

    let contactMap: Record<
      number,
      {
        first_name: string;
        last_name: string;
        current_score?: number;
        current_tier?: string;
      }
    > = {};

    if (contactIds.length > 0) {
      const { data: contactData, error: contactError } = await supabase
        .from("contacts")
        .select("id, first_name, last_name, current_score, current_tier")
        .in("id", contactIds);

      if (contactError) {
        console.error("Failed to load contact names:", contactError);
        // Non-fatal — tasks still show without contact names
      } else if (contactData) {
        contactMap = Object.fromEntries(contactData.map((c: any) => [c.id, c]));
      }
    }

    setTasks(
      activeTasks.map((t: TaskRecord) => ({
        ...t,
        contacts:
          t.contact_id != null ? (contactMap[t.contact_id] ?? null) : null,
      })),
    );
  }, [salesId, showAll]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const tasksByStatus = (status: TaskStatus) => {
    const filtered = tasks.filter((t) => getTaskStatus(t) === status);
    // For "done", only show tasks completed within the last 7 days
    if (status === "done") {
      return filtered.filter(
        (t) =>
          !t.completed_at ||
          new Date(t.completed_at) > new Date(Date.now() - 7 * 86400000),
      );
    }
    return filtered;
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;
    const taskId = parseInt(result.draggableId);
    const newStatus = result.destination.droppableId as TaskStatus;
    const currentStatus = result.source.droppableId as TaskStatus;

    if (newStatus === currentStatus) return;

    const now = new Date().toISOString();
    const updates: Record<string, string | null> = {};

    if (newStatus === "done") {
      updates.type = "done";
      updates.done_date = now;
      updates.completed_at = now;
    } else if (newStatus === "in_progress") {
      updates.type = "in_progress";
      updates.done_date = null;
      updates.completed_at = null;
    } else {
      updates.type = "todo";
      updates.done_date = null;
      updates.completed_at = null;
    }

    // Optimistic update
    setTasks((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, ...updates } : t)),
    );

    const { error } = await supabase
      .from("tasks")
      .update(updates)
      .eq("id", taskId);

    if (error) {
      console.error("Failed to update task:", error);
      // Revert optimistic update by reloading
      loadTasks();
    }
  };

  const handleDeleteTask = async (taskId: number) => {
    // Optimistic removal
    setTasks((prev) => prev.filter((t) => t.id !== taskId));

    const { error } = await supabase.from("tasks").delete().eq("id", taskId);

    if (error) {
      console.error("Failed to delete task:", error);
      // Revert by reloading
      loadTasks();
    }
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold">
          {showAll ? "All Tasks" : "My Tasks"}
        </h2>
        <Button size="sm" onClick={() => setShowCreate(true)}>
          New Task
        </Button>
      </div>

      {loadError && (
        <p className="mb-4 rounded border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
          Failed to load tasks: {loadError}
        </p>
      )}

      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="grid grid-cols-3 gap-4">
          {columns.map((col) => (
            <Droppable key={col.id} droppableId={col.id}>
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`min-h-[200px] rounded-lg border p-3 ${snapshot.isDraggingOver ? "bg-accent" : "bg-muted/30"}`}
                >
                  <h3 className="mb-3 text-sm font-medium text-muted-foreground">
                    {col.title} ({tasksByStatus(col.id).length})
                  </h3>
                  {tasksByStatus(col.id).map((task, index) => (
                    <Draggable
                      key={task.id}
                      draggableId={String(task.id)}
                      index={index}
                    >
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className="mb-2"
                        >
                          <TaskCard task={task} onDelete={handleDeleteTask} />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          ))}
        </div>
      </DragDropContext>

      {showCreate && (
        <TaskCreateDialog
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            loadTasks();
          }}
        />
      )}
    </div>
  );
}
